/**
 * F.6.2 · Pagina Dettaglio Contratto di Subappalto.
 * Ospita tab "Modifiche Contrattuali" (F.6.2).
 * Route: /subcontracts/:id
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import ContractModificationsTab from '../components/contract-modifications/ContractModificationsTab';
import SupplierInvoicesTab from '../components/supplier-invoices/SupplierInvoicesTab';
import SubcontractDocumentsCard from '../components/subcontract/SubcontractDocumentsCard';
import { fmtEUR } from '../lib/contractModificationApi';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem('token');
  return { headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

const STATUS_LABELS = {
  bozza: 'Bozza', pronto_per_firma: 'Pronto per firma', firmato: 'Firmato',
  annullato: 'Annullato', risolto: 'Risolto', chiuso: 'Chiuso',
};

const SubcontractDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usersMap, setUsersMap] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/subcontracts/${id}`, auth());
      setSub(r.data);
    } catch (e) {
      toast.error('Contratto non trovato');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/users`, auth());
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.users || []);
        const m = {};
        list.forEach((u) => {
          const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.full_name || u.name || u.email;
          if (u.id) m[u.id] = name;
        });
        setUsersMap(m);
      } catch { /* opzionale: fallback all'id */ }
    })();
  }, []);

  if (loading) return <div className="p-6 text-sm text-slate-500">Caricamento…</div>;
  if (!sub) return <div className="p-6">
    <Button variant="ghost" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-1" /> Indietro</Button>
    <p className="mt-4 text-slate-600">Contratto non disponibile.</p>
  </div>;

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="subcontract-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="sd-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
          </Button>
          <h1 className="text-2xl font-semibold mt-2 flex items-center gap-2">
            <span data-testid="sd-number">{sub.subcontract_number}</span>
            <Badge className="bg-teal-100 text-teal-800 border-teal-400" data-testid="sd-revision">
              Rev.{sub.contract_revision ?? 1}
            </Badge>
            <Badge variant="outline" data-testid="sd-status">{STATUS_LABELS[sub.status] || sub.status}</Badge>
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {sub.supplier_snapshot?.ragione_sociale || sub.supplier_id} · Importo attuale: <strong>{fmtEUR(sub.total_amount_current ?? sub.total_amount_net)}</strong>
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={load} data-testid="sd-reload">
          <RefreshCw className="w-3 h-3" />
        </Button>
      </div>

      {/* Card primaria: documenti e firma contratto (sempre visibile) */}
      <SubcontractDocumentsCard subcontract={sub} onChanged={load} usersMap={usersMap} />

      {/* Tabs */}
      <Tabs defaultValue="modifications" className="w-full">
        <TabsList data-testid="sd-tabs">
          <TabsTrigger value="modifications" data-testid="sd-tab-modifications">Modifiche Contrattuali</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="sd-tab-invoices">Fatture Fornitore</TabsTrigger>
          <TabsTrigger value="summary" data-testid="sd-tab-summary">Riepilogo</TabsTrigger>
        </TabsList>
        <TabsContent value="modifications" className="mt-4">
          <ContractModificationsTab subcontract={sub} onSubcontractChanged={load} />
        </TabsContent>
        <TabsContent value="invoices" className="mt-4">
          <SupplierInvoicesTab subcontract={sub} onSubcontractChanged={load} />
        </TabsContent>
        <TabsContent value="summary" className="mt-4">
          <div className="rounded-md border bg-white p-4 text-sm space-y-1" data-testid="sd-summary">
            <div><strong>Progetto:</strong> {sub.project_id}</div>
            <div><strong>Contratto cliente:</strong> {sub.customer_contract_id || '—'}</div>
            <div><strong>Origine Offerta:</strong> {sub.source_offer_number || '—'} Rev.{sub.source_offer_revision ?? '—'}</div>
            <div><strong>Importo originario (Rev.1):</strong> {fmtEUR(sub.total_amount_net)}</div>
            <div><strong>Importo attuale (Rev.{sub.contract_revision ?? 1}):</strong> {fmtEUR(sub.total_amount_current)}</div>
            <div><strong>Prossimo M##:</strong> M{String(sub.next_modification_sequence ?? 1).padStart(2, '0')}</div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SubcontractDetailPage;
